import { CustomCellEditorProps } from "@ag-grid-community/react";
import { FocusableElement } from "@chakra-ui/utils";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getMaybernTableCellError } from "shared/components/table/maybern/MaybernTableErrors";

export interface MaybernTableCellEditorOptions<T> {
  setStartValue?: (args: {
    inputRef: React.RefObject<T>;
    startValue: string;
    onValueChange: (value: string) => void;
  }) => void;
}

export const useMaybernTableCellEditorProps = <
  T extends FocusableElement = HTMLInputElement,
>(
  {
    value: valueProp,
    onValueChange,
    eventKey,
    column,
    node,
    context,
  }: CustomCellEditorProps,
  { setStartValue }: MaybernTableCellEditorOptions<T> = {},
) => {
  const inputRef = useRef<T>(null);
  const [didSetStartValue, setDidSetStartValue] = useState(false);

  const startValue = useMemo(() => {
    if (eventKey === "Backspace") {
      return "";
    } else if (eventKey?.length === 1) {
      return eventKey;
    }
    return null;
  }, [eventKey]);

  const value =
    (startValue && !didSetStartValue ? startValue : valueProp) ?? "";
  const error = getMaybernTableCellError({ column, node, context });

  useEffect(() => {
    if (didSetStartValue) {
      return;
    }
    // explicitly check for null because "" is a valid value
    if (startValue != null) {
      if (setStartValue) {
        setStartValue({ inputRef, startValue, onValueChange });
      } else {
        onValueChange(startValue);
      }
      setDidSetStartValue(true);
    }
    inputRef.current?.focus();
  }, [didSetStartValue, onValueChange, setStartValue, startValue]);

  return { inputRef, value, onValueChange, error };
};
